import React, { useRef, useMemo, Suspense } from 'react';
import { useNavigate } from 'react-router-dom';
import { Canvas, useFrame } from '@react-three/fiber';
import {
  OrbitControls,
  PerspectiveCamera,
  Text,
  Float,
  MeshDistortMaterial,
  Sphere
} from '@react-three/drei';
import { EffectComposer, Bloom, ChromaticAberration } from '@react-three/postprocessing';
import { BlendFunction } from 'postprocessing';
import * as THREE from 'three';
import TypeWriterEffect from 'react-typewriter-effect';
import './heroSection.scss';

// Neural Network Background Component
const NeuralNetwork = () => {
  const points = useRef();
  const lines = useRef();

  const particlesData = useMemo(() => {
    const particles = [];
    const particleCount = 300;

    for (let i = 0; i < particleCount; i++) {
      particles.push({
        position: new THREE.Vector3(
          (Math.random() - 0.5) * 40,
          (Math.random() - 0.5) * 40,
          (Math.random() - 0.5) * 40
        ),
        velocity: new THREE.Vector3(
          (Math.random() - 0.5) * 0.02,
          (Math.random() - 0.5) * 0.02,
          (Math.random() - 0.5) * 0.02
        )
      });
    }
    return particles;
  }, []);

  const positions = useMemo(() => {
    const pos = new Float32Array(particlesData.length * 3);
    particlesData.forEach((p, i) => {
      pos[i * 3] = p.position.x;
      pos[i * 3 + 1] = p.position.y;
      pos[i * 3 + 2] = p.position.z;
    });
    return pos;
  }, [particlesData]);

  const linePositions = useMemo(() => {
    const linePos = [];
    const maxDistance = 5;

    for (let i = 0; i < particlesData.length; i++) {
      for (let j = i + 1; j < particlesData.length; j++) {
        const dist = particlesData[i].position.distanceTo(particlesData[j].position);
        if (dist < maxDistance) {
          linePos.push(
            particlesData[i].position.x, particlesData[i].position.y, particlesData[i].position.z,
            particlesData[j].position.x, particlesData[j].position.y, particlesData[j].position.z
          );
        }
      }
    }
    return new Float32Array(linePos);
  }, [particlesData]);

  useFrame((state) => {
    const time = state.clock.elapsedTime;

    particlesData.forEach((p, i) => {
      p.position.add(p.velocity);

      if (Math.abs(p.position.x) > 20) p.velocity.x *= -1;
      if (Math.abs(p.position.y) > 20) p.velocity.y *= -1;
      if (Math.abs(p.position.z) > 20) p.velocity.z *= -1;

      positions[i * 3] = p.position.x;
      positions[i * 3 + 1] = p.position.y;
      positions[i * 3 + 2] = p.position.z;
    });

    if (points.current) {
      points.current.geometry.attributes.position.needsUpdate = true;
    }

    if (lines.current) {
      lines.current.rotation.y = time * 0.05;
    }
  });

  return (
    <group>
      <points ref={points}>
        <bufferGeometry>
          <bufferAttribute
            attach="attributes-position"
            count={positions.length / 3}
            array={positions}
            itemSize={3}
          />
        </bufferGeometry>
        <pointsMaterial
          size={0.15}
          color="#8b5cf6"
          transparent
          opacity={0.8}
          sizeAttenuation
          blending={THREE.AdditiveBlending}
        />
      </points>

      <lineSegments ref={lines}>
        <bufferGeometry>
          <bufferAttribute
            attach="attributes-position"
            count={linePositions.length / 3}
            array={linePositions}
            itemSize={3}
          />
        </bufferGeometry>
        <lineBasicMaterial
          color="#6366f1"
          transparent
          opacity={0.2}
          blending={THREE.AdditiveBlending}
        />
      </lineSegments>
    </group>
  );
};

// Crystalline Brain Focal Point
const CrystallineBrain = () => {
  const brainRef = useRef();
  const innerGlowRef = useRef();

  useFrame((state) => {
    const time = state.clock.elapsedTime;
    if (brainRef.current) {
      brainRef.current.rotation.y = time * 0.3;
      brainRef.current.rotation.x = Math.sin(time * 0.5) * 0.1;
    }
    if (innerGlowRef.current) {
      innerGlowRef.current.rotation.y = -time * 0.2;
      const scale = 1 + Math.sin(time * 2) * 0.05;
      innerGlowRef.current.scale.set(scale, scale, scale);
    }
  });

  return (
    <group position={[0, 0, 0]}>
      {/* Inner glow */}
      <Sphere ref={innerGlowRef} args={[1.2, 32, 32]}>
        <meshBasicMaterial
          color="#ec4899"
          transparent
          opacity={0.3}
          blending={THREE.AdditiveBlending}
        />
      </Sphere>

      {/* Main crystal brain */}
      <Sphere ref={brainRef} args={[1, 64, 64]}>
        <MeshDistortMaterial
          color="#8b5cf6"
          emissive="#ec4899"
          emissiveIntensity={0.5}
          transparent
          opacity={0.7}
          roughness={0.2}
          metalness={0.8}
          distort={0.4}
          speed={2}
        />
      </Sphere>

      {/* Outer particles */}
      {[...Array(20)].map((_, i) => (
        <Float
          key={i}
          speed={2 + Math.random() * 2}
          rotationIntensity={0.5}
          floatIntensity={0.5}
        >
          <Sphere
            position={[
              Math.cos((i / 20) * Math.PI * 2) * 2,
              Math.sin((i / 10) * Math.PI) * 1.5,
              Math.sin((i / 20) * Math.PI * 2) * 2
            ]}
            args={[0.05, 16, 16]}
          >
            <meshBasicMaterial
              color="#ec4899"
              transparent
              opacity={0.8}
            />
          </Sphere>
        </Float>
      ))}
    </group>
  );
};

// Feature Node Component
const FeatureNode = ({ position, label, icon }) => {
  const meshRef = useRef();
  const [hovered, setHovered] = React.useState(false);

  useFrame((state) => {
    const time = state.clock.elapsedTime;
    if (meshRef.current) {
      meshRef.current.rotation.y = time * 0.5;
      const scale = hovered ? 1.2 : 1;
      meshRef.current.scale.lerp(new THREE.Vector3(scale, scale, scale), 0.1);
    }
  });

  return (
    <Float speed={1.5} rotationIntensity={0.3} floatIntensity={0.5}>
      <group position={position}>
        <mesh
          ref={meshRef}
          onPointerOver={() => setHovered(true)}
          onPointerOut={() => setHovered(false)}
        >
          <octahedronGeometry args={[0.4, 0]} />
          <meshStandardMaterial
            color={hovered ? "#ec4899" : "#8b5cf6"}
            emissive={hovered ? "#ec4899" : "#6366f1"}
            emissiveIntensity={hovered ? 1 : 0.5}
            roughness={0.3}
            metalness={0.8}
          />
        </mesh>
        <Text
          position={[0, -0.8, 0]}
          fontSize={0.15}
          color="#ffffff"
          anchorX="center"
          anchorY="middle"
          outlineWidth={0.02}
          outlineColor="#000000"
        >
          {label}
        </Text>
      </group>
    </Float>
  );
};

// 3D Scene Component
const Scene3D = () => {
  return (
    <>
      <PerspectiveCamera makeDefault position={[0, 0, 10]} fov={75} />
      <ambientLight intensity={0.3} />
      <pointLight position={[10, 10, 10]} intensity={1} color="#8b5cf6" />
      <pointLight position={[-10, -10, -10]} intensity={0.5} color="#ec4899" />

      <NeuralNetwork />

      {/* Feature Nodes
      <FeatureNode
        position={[-4, 2, 0]}
        label="AI Extraction"
      />
      <FeatureNode
        position={[4, 2, 0]}
        label="Knowledge Graphs"
      />
      <FeatureNode
        position={[-4, -2, 0]}
        label="Graphical Abstracts"
      />
      <FeatureNode
        position={[4, -2, 0]}
        label="Biomedical Insights"
      /> */}

      <OrbitControls
        enableZoom={false}
        enablePan={false}
        enableRotate={false}
        minPolarAngle={Math.PI / 3}
        maxPolarAngle={Math.PI / 1.5}
        autoRotate
        autoRotateSpeed={0.3}
      />

      <EffectComposer>
        <Bloom
          intensity={1.5}
          luminanceThreshold={0.3}
          luminanceSmoothing={0.9}
          blendFunction={BlendFunction.ADD}
        />
        <ChromaticAberration offset={[0.001, 0.001]} />
      </EffectComposer>
    </>
  );
};

// Headline typed out on load, and the tagline revealed once it finishes
const HERO_HEADLINE = 'Create Graphical Abstracts in Minutes...';
const HERO_TAGLINE = 'Turn your findings into a publication-ready figure, styled for journals and grants.';
const TYPE_SPEED_MS = 50;
const TYPE_START_DELAY_MS = 200;

// Main Hero Section Component
const HeroSection = () => {
  const navigate = useNavigate();
  const [showTagline, setShowTagline] = React.useState(false);

  // Hold the tagline back until the headline has finished typing - a static
  // line appearing beside an animating one reads as a layout glitch
  React.useEffect(() => {
    const revealDelay = TYPE_START_DELAY_MS + (HERO_HEADLINE.length * TYPE_SPEED_MS) + 300;
    const timer = setTimeout(() => setShowTagline(true), revealDelay);
    return () => clearTimeout(timer);
  }, []);

  const handleBrainSnapClick = () => {
    navigate('/brain-snap');
  };

  const handleScrollDown = () => {
    window.scrollBy({
      top: window.innerHeight,
      behavior: 'smooth'
    });
  };

  return (
    <div className="hero-section">
      <div className="hero-canvas-container">
        <Canvas>
          <Suspense fallback={null}>
            <Scene3D />
          </Suspense>
        </Canvas>
      </div>

      <div className="hero-content">
        <div className="hero-headline">
          <h1 className="hero-title">
            <TypeWriterEffect
              textStyle={{
                fontFamily: 'Nunito Sans',
                fontWeight: 500,
                color: '#fff', // Use white/gradient via CSS
                fontSize: 'inherit',
              }}
              startDelay={TYPE_START_DELAY_MS}
              cursorColor="#ec4899"
              text={HERO_HEADLINE}
              typeSpeed={TYPE_SPEED_MS}
              scrollArea={document}
            />
          </h1>
          <p className={`hero-tagline ${showTagline ? 'is-visible' : ''}`}>
            {HERO_TAGLINE}
          </p>
          <div className="hero-logo" onClick={handleBrainSnapClick}>BrainSNAP</div>
        </div>
      </div>

      <button className="scroll-down-btn" onClick={handleScrollDown} aria-label="Scroll down">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <polyline points="6 9 12 15 18 9"></polyline>
        </svg>
      </button>
    </div>
  );
};

export default HeroSection;
